import React, { useEffect, useRef, useState } from 'react';
import { formatMetricaDate } from '../../utils/dateUtils';
import {
  HEBREW_MONTHS,
  MAX_RECURRENCE_COUNT,
  RecurrenceConfig,
  RecurrenceWeekDay,
  WEEK_DAYS,
  daysInMonth,
  yearFromIsoDate,
} from '../../types/recurrence';
import editorStyles from '../../pages/ExpenseEditorPage.module.css';
import styles from './RecurrencePopup.module.css';

interface RecurrencePopupProps {
  config: RecurrenceConfig;
  onChange: (config: RecurrenceConfig) => void;
  onConfirm: () => void;
  onCancel: () => void;
  onRemove: () => void;
  position: { x: number; y: number };
  onHeaderMouseDown: (event: React.MouseEvent) => void;
}

const RecurrenceDateField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}> = ({ value, onChange, ariaLabel }) => (
  <div className={`${editorStyles.metricaDateField} ${styles.dateField} ${styles.sentenceDate}`.trim()}>
    <span
      className={`${editorStyles.metricaDateDisplay} ${!value ? editorStyles.metricaDatePlaceholder : ''}`.trim()}
      aria-hidden
    >
      {formatMetricaDate(value)}
    </span>
    <div className={editorStyles.metricaDateIcon} aria-hidden>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
      </svg>
    </div>
    <input
      type="date"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={editorStyles.metricaDateNative}
      aria-label={ariaLabel}
    />
  </div>
);

const CompactTextSelect: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  compact?: boolean;
}> = ({ value, options, onChange, ariaLabel, className = '', compact = false }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    <div className={`${styles.compactSelect} ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className={`${styles.textSelect} ${compact ? styles.textSelectDay : ''}`.trim()}
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        {selected?.label ?? value}
      </button>
      {open && (
        <ul
          className={`${styles.compactMenu} ${compact ? styles.compactMenuTight : ''}`.trim()}
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={`${styles.compactOption} ${option.value === value ? styles.compactOptionSelected : ''}`.trim()}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function RecurrencePopup({
  config,
  onChange,
  onConfirm,
  onCancel,
  onRemove,
  position,
  onHeaderMouseDown,
}: RecurrencePopupProps) {
  const update = (patch: Partial<RecurrenceConfig>) => onChange({ ...config, ...patch });

  const renderDayMonthFields = () => (
    <span className={styles.inlineFields}>
      כל
      <CompactTextSelect
        compact
        className={styles.textSelectDay}
        value={String(config.monthDay)}
        ariaLabel="יום בחודש"
        options={Array.from(
          { length: daysInMonth(config.yearMonth, yearFromIsoDate(config.startDate)) },
          (_, index) => ({
            value: String(index + 1),
            label: String(index + 1),
          })
        )}
        onChange={(day) => update({ monthDay: Number(day) })}
      />
      <CompactTextSelect
        className={styles.textSelectMonth}
        value={String(config.yearMonth)}
        ariaLabel="חודש"
        options={HEBREW_MONTHS.map((month, index) => ({
          value: String(index + 1),
          label: `ל${month}`,
        }))}
        onChange={(month) => {
          const yearMonth = Number(month);
          const maxDay = daysInMonth(yearMonth, yearFromIsoDate(config.startDate));
          update({
            yearMonth,
            monthDay: Math.min(config.monthDay, maxDay),
          });
        }}
      />
    </span>
  );

  return (
    <div
      className={styles.popup}
      style={{ left: position.x, top: position.y }}
      role="dialog"
      aria-label="הגדרות הוצאה חוזרת"
    >
      <div className={styles.header} onMouseDown={onHeaderMouseDown}>
        <span className={styles.title}>הוצאה חוזרת</span>
        <button type="button" className={styles.closeButton} onClick={onCancel} aria-label="סגור">
          ×
        </button>
      </div>

      <div className={styles.body}>
        <section className={styles.section}>
          <div className={styles.patternRow}>
            <div className={styles.frequencyList} role="radiogroup" aria-label="תדירות">
              <label className={`${styles.radioRow} ${styles.sentenceRow}`.trim()}>
                <input
                  type="radio"
                  name="recurrence-frequency"
                  checked={config.frequency === 'weekly'}
                  onChange={() => update({ frequency: 'weekly' })}
                />
                <span>שבועי</span>
                {config.frequency === 'weekly' && (
                  <span className={styles.inlineFields}>
                    כל
                    <select
                      className={styles.textSelect}
                      value={config.weekDay}
                      onChange={(e) => update({ weekDay: e.target.value as RecurrenceWeekDay })}
                      aria-label="יום בשבוע"
                    >
                      {WEEK_DAYS.map((day) => (
                        <option key={day.id} value={day.id}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </span>
                )}
              </label>
              <label className={`${styles.radioRow} ${styles.sentenceRow}`.trim()}>
                <input
                  type="radio"
                  name="recurrence-frequency"
                  checked={config.frequency === 'monthly'}
                  onChange={() => update({ frequency: 'monthly' })}
                />
                <span>חודשי</span>
                {config.frequency === 'monthly' && (
                  <span className={styles.inlineFields}>
                    כל
                    <CompactTextSelect
                      compact
                      className={styles.textSelectDay}
                      value={String(config.monthDay)}
                      ariaLabel="יום בחודש"
                      options={Array.from({ length: 31 }, (_, index) => ({
                        value: String(index + 1),
                        label: String(index + 1),
                      }))}
                      onChange={(day) => update({ monthDay: Number(day) })}
                    />
                    בחודש
                  </span>
                )}
              </label>
              <label className={`${styles.radioRow} ${styles.sentenceRow}`.trim()}>
                <input
                  type="radio"
                  name="recurrence-frequency"
                  checked={config.frequency === 'yearly'}
                  onChange={() => update({ frequency: 'yearly' })}
                />
                <span>שנתי</span>
                {config.frequency === 'yearly' && renderDayMonthFields()}
              </label>
            </div>
          </div>
        </section>

        <hr className={styles.blockDivider} />

        <section className={styles.section}>
          <div className={styles.frequencyList} role="radiogroup" aria-label="סיום חזרה">
            <label className={`${styles.radioRow} ${styles.sentenceRow}`.trim()}>
              <input
                type="radio"
                name="recurrence-end-type"
                checked={config.endType === 'byDate'}
                onChange={() => update({ endType: 'byDate' })}
              />
              <span className={styles.rangeSentence}>
                מתחיל
                <RecurrenceDateField
                  value={config.startDate}
                  onChange={(startDate) => {
                    const maxDay = daysInMonth(config.yearMonth, yearFromIsoDate(startDate));
                    update({
                      startDate,
                      monthDay: Math.min(config.monthDay, maxDay),
                      endType: 'byDate',
                    });
                  }}
                  ariaLabel="תאריך התחלה"
                />
                מסתיים
                <RecurrenceDateField
                  value={config.endDate}
                  onChange={(endDate) => update({ endDate, endType: 'byDate' })}
                  ariaLabel="תאריך סיום"
                />
              </span>
            </label>
            <label className={`${styles.radioRow} ${styles.sentenceRow}`.trim()}>
              <input
                type="radio"
                name="recurrence-end-type"
                checked={config.endType === 'afterCount'}
                onChange={() => update({ endType: 'afterCount' })}
              />
              <span className={styles.rangeSentence}>
                מסתיים אחרי
                <input
                  type="text"
                  inputMode="numeric"
                  className={styles.countInput}
                  value={config.count}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    if (digits === '') {
                      update({ count: 1, endType: 'afterCount' });
                      return;
                    }
                    update({
                      count: Math.min(MAX_RECURRENCE_COUNT, Math.max(1, Number(digits))),
                      endType: 'afterCount',
                    });
                  }}
                  aria-label="מספר חזרות"
                />
                חזרות
              </span>
            </label>
          </div>
        </section>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerSaveGroup}>
          <button type="button" className={styles.confirmButton} onClick={onConfirm}>
            אישור
          </button>
          <button type="button" className={styles.cancelButton} onClick={onCancel}>
            ביטול
          </button>
        </div>
        <button type="button" className={styles.removeButton} onClick={onRemove}>
          הסר חזרה
        </button>
      </div>
    </div>
  );
}
